import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Copy, GitBranch, Radio } from 'lucide-react';
import { fetchChannel, fetchSignals, type FetchedSignal } from '../../api/dataApi';
import { PoweredByZmistBadge } from '../../components/ui/PoweredByZmistBadge';
import { AppCard } from '../../components/ui/AppCard';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { rememberPostLoginRedirect, useStore } from '../../store/StoreContext';
import type { Channel } from '../../types';

// Public, unauthenticated route (see App.tsx - same "outside RequireGuest/
// RequireAuth/AppLayout" placement as PublicChannelViewScreen, and the same
// reason: /status is a marketing/trust asset meant to be opened by someone
// with no Zmist account at all, linked from the landing site's footer).
//
// This is the "Zmist runs on Zmist" roadmap item (docs/CLAUDE_ZMIST_
// ROADMAP.md, Phase 0): two real public channels - #status (infra gauges,
// posted by ops/status-report.sh on a cron) and #engineering (deploy/CI/PR
// signals, posted by .github/workflows/zmist-engineering-signals.yml) -
// bundled into one page instead of sending a visitor to two separate
// shared-channel links.
//
// Deliberately does NOT go through the share-link mechanism (fetchShared
// Channel/POST /shared/{nickname}/{token}) the way PublicChannelViewScreen
// does: that flow exists for channels an owner has selectively exposed via
// a rotatable secret token. #status/#engineering are meant to be plainly,
// permanently public, so this reads them the same way any public channel
// already is: GET /channel/{nickname} has no auth check at all
// (backend/workflows/channel/read.wsl), and GET /signals/{channelId}
// resolves an anonymous caller through to its "is this channel public"
// check same as any other viewer (see PublicChannelViewScreen.tsx's
// `contentUnlocked` comment for the identical reasoning) - no token to
// mint, rotate, or leak.
//
// Channel nicknames are overridable via env var so this doesn't hard-fail
// if `status`/`engineering` are already taken on a given deploy.
const STATUS_CHANNEL = import.meta.env.VITE_STATUS_CHANNEL_NICKNAME || 'status';
const ENGINEERING_CHANNEL = import.meta.env.VITE_ENGINEERING_CHANNEL_NICKNAME || 'engineering';

// Metric signal `type`s ops/status-report.sh posts, in display order.
// Percentages and a latency number only - deliberately nothing that could
// fingerprint the underlying infra (no hostnames, provider, region, node
// counts - see the cron script's own header comment).
const STATUS_METRICS: { type: string; label: string; unit: string }[] = [
  { type: 'cpu_pct', label: 'CPU', unit: '%' },
  { type: 'mem_pct', label: 'Memory', unit: '%' },
  { type: 'disk_pct', label: 'Disk', unit: '%' },
  { type: 'latency_ms', label: 'API latency', unit: 'ms' },
  { type: 'error_rate_pct', label: 'Error rate', unit: '%' },
  { type: 'uptime_hours', label: 'Uptime', unit: 'h' },
];

// Signal `type`s .github/workflows/zmist-engineering-signals.yml posts.
const ENGINEERING_LABELS: Record<string, string> = {
  deploy: 'Deploy',
  ci_pass: 'CI passed',
  ci_fail: 'CI failed',
  pr_merged: 'PR merged',
};

function latestByType(signals: FetchedSignal[], type: string): FetchedSignal | undefined {
  // `signals` arrives newest-first (see signal/list.wsl's header comment),
  // so the first match for a given metric type is its latest reading.
  return signals.find((s) => s.type === type);
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export function StatusReportScreen() {
  const navigate = useNavigate();
  const { isLoggedIn, cloneChannel } = useStore();
  const [statusChannel, setStatusChannel] = useState<Channel | null>(null);
  const [engineeringChannel, setEngineeringChannel] = useState<Channel | null>(null);
  const [statusSignals, setStatusSignals] = useState<FetchedSignal[]>([]);
  const [engineeringSignals, setEngineeringSignals] = useState<FetchedSignal[]>([]);
  const [loading, setLoading] = useState(true);
  const [cloningId, setCloningId] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState('');

  // "Clone" here is the roadmap's own stated point of this whole page
  // ("Clone-of-channels... turning the demo into an acquisition funnel"):
  // a visitor who sees #status/#engineering and thinks "I want this for my
  // own project" gets a real starting point, not just a screenshot. Same
  // client-side clone (build a draft, POST it, 409-retry) cloneChannel
  // already does everywhere else - see dataApi's cloneChannel/
  // PublicChannelViewScreen.tsx's identical clone() for the pattern this
  // mirrors.
  const clone = async (source: Channel) => {
    if (!isLoggedIn) {
      rememberPostLoginRedirect('/status');
      navigate('/register');
      return;
    }
    setCloningId(source.id);
    setCloneError('');
    try {
      const created = await cloneChannel(source, 'status_page');
      navigate(`/channels/${created.id}/edit`);
    } catch (err) {
      setCloneError(err instanceof Error ? err.message : 'Unable to clone this channel.');
    } finally {
      setCloningId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [statusCh, engineeringCh, statusPage, engineeringPage] = await Promise.all([
        fetchChannel(STATUS_CHANNEL),
        fetchChannel(ENGINEERING_CHANNEL),
        fetchSignals(STATUS_CHANNEL, 0, 50),
        fetchSignals(ENGINEERING_CHANNEL, 0, 20),
      ]);
      if (cancelled) return;
      setStatusChannel(statusCh);
      setEngineeringChannel(engineeringCh);
      setStatusSignals(statusPage.signals);
      setEngineeringSignals(engineeringPage.signals);
      setLoading(false);
    })().catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const lastSampleAt = useMemo(() => statusSignals[0]?.date, [statusSignals]);
  const bothMissing = !loading && !statusChannel && !engineeringChannel;

  return (
    <div className="min-h-screen bg-[#f4f4f6] dark:bg-black flex flex-col items-center px-4 py-8">
      <div className="w-full max-w-2xl flex flex-col gap-5">
        <div className="flex flex-col items-center text-center gap-2">
          <Radio size={28} className="text-black/40 dark:text-white/40" />
          <h1 className="text-2xl font-bold m-0">Zmist runs on Zmist</h1>
          <p className="text-sm text-black/50 dark:text-white/50 m-0 max-w-md">
            Live infrastructure and engineering activity for Zmist itself, tracked the same way any Zmist customer
            tracks theirs — no dashboard, just signals.
          </p>
          <PoweredByZmistBadge />
        </div>

        {loading && <span className="text-sm text-black/50 dark:text-white/50 text-center">Loading…</span>}

        {bothMissing && (
          <EmptyState
            icon={Radio}
            title="Not live yet"
            subtitle="This deploy hasn't set up the #status and #engineering public channels yet."
          />
        )}

        {!loading && statusChannel && (
          <AppCard className="flex flex-col gap-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Activity size={18} className="text-black/50 dark:text-white/50" />
              <span className="font-semibold">#{statusChannel.nickname}</span>
              <div className="ml-auto flex items-center gap-2">
                {lastSampleAt && (
                  <span className="text-xs text-black/40 dark:text-white/40">Updated {timeAgo(lastSampleAt)}</span>
                )}
                <Button
                  variant="bordered"
                  onClick={() => clone(statusChannel)}
                  loading={cloningId === statusChannel.id}
                  disabled={cloningId !== null}
                >
                  <Copy size={14} />
                  Clone
                </Button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {STATUS_METRICS.map((metric) => {
                const latest = latestByType(statusSignals, metric.type);
                return (
                  <div
                    key={metric.type}
                    className="rounded-xl bg-black/[0.03] dark:bg-white/[0.04] px-3 py-2.5 flex flex-col gap-0.5"
                  >
                    <span className="text-xs text-black/50 dark:text-white/50">{metric.label}</span>
                    <span className="text-lg font-semibold tabular-nums">
                      {latest?.value !== undefined ? `${latest.value}${metric.unit}` : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          </AppCard>
        )}

        {!loading && engineeringChannel && (
          <AppCard className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <GitBranch size={18} className="text-black/50 dark:text-white/50" />
              <span className="font-semibold">#{engineeringChannel.nickname}</span>
              <Button
                variant="bordered"
                className="ml-auto"
                onClick={() => clone(engineeringChannel)}
                loading={cloningId === engineeringChannel.id}
                disabled={cloningId !== null}
              >
                <Copy size={14} />
                Clone
              </Button>
            </div>
            {engineeringSignals.length === 0 ? (
              <EmptyState icon={GitBranch} title="No activity yet" subtitle="Deploys and CI runs will show up here." />
            ) : (
              <div className="flex flex-col gap-2">
                {engineeringSignals.map((signal) => (
                  <div key={signal.id} className="flex items-center gap-3 text-sm">
                    <span className="text-black/40 dark:text-white/40 text-xs shrink-0 w-14">{timeAgo(signal.date)}</span>
                    <span className="font-medium">{ENGINEERING_LABELS[signal.type] ?? signal.type}</span>
                    {signal.text && <span className="text-black/50 dark:text-white/50 truncate">{signal.text}</span>}
                  </div>
                ))}
              </div>
            )}
          </AppCard>
        )}

        {cloneError && <span className="text-sm text-red-500 text-center">{cloneError}</span>}
      </div>
    </div>
  );
}
